import { FC } from 'react';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { isDateFieldType, isSelectFieldType } from '@/components/UI/DataTableV2/DataTable.util';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { useCellEditability } from '@/components/UI/DataTableV2/hooks/useCellEditability';
import { DataTableDateCell } from './DataTableDateCell';
import { SampleCollectedAtCell } from './SampleCollectedAtCell';
import { DataTableDefaultCell } from './DataTableDefaultCell';
import { DataTableTimestampCell } from './DataTableTimestampCell';
import { DataTableReadonlyCell } from './DataTableReadonlyCell';
import { MetadataSelectCell } from './MetadataSelectCell';
import { SampleDoseTimeCell } from '@/components/UI/DataTableV2/cells/SampleDoseTimeCell';
import { SampleStatusCell } from '@/components/UI/DataTableV2/cells/SampleStatusCell';

interface CellTypeRouterProps {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
}

export const CellTypeRouter: FC<CellTypeRouterProps> = ({ coordinate, positionOffset }) => {
  const { dataTableService } = useDataTable();
  const { isEditable } = useCellEditability(coordinate);
  const column = dataTableService.columnByIndex(coordinate.column);

  if (!isEditable && column?.type !== 'sampleStatus') {
    return <DataTableReadonlyCell coordinate={coordinate} positionOffset={positionOffset} />;
  }

  switch (column?.type) {
    case 'sampleCollectedAt':
      return <SampleCollectedAtCell coordinate={coordinate} positionOffset={positionOffset} />;

    case 'timestamp':
    case 'timestampBaseline':
      return <DataTableTimestampCell coordinate={coordinate} positionOffset={positionOffset} />;
    case 'samplePlannedAt':
    case 'timestampBaselineRelative':
    case 'formula':
    case 'observation':
      return <DataTableReadonlyCell coordinate={coordinate} positionOffset={positionOffset} />;

    case 'measurement':
      if (column.measurement?.is_calculated ?? false) {
        return <DataTableReadonlyCell coordinate={coordinate} positionOffset={positionOffset} />;
      }
      return <DataTableDefaultCell coordinate={coordinate} positionOffset={positionOffset} />;

    case 'metadata': {
      if (isDateFieldType(column.metadata?.field_type)) {
        return <DataTableDateCell coordinate={coordinate} positionOffset={positionOffset} />;
      }
      if (isSelectFieldType(column.metadata?.field_type)) {
        return <MetadataSelectCell coordinate={coordinate} positionOffset={positionOffset} />;
      }
      return <DataTableReadonlyCell coordinate={coordinate} positionOffset={positionOffset} />;
    }
    case 'sampleStatus': {
      return <SampleStatusCell coordinate={coordinate} positionOffset={positionOffset} />;
    }
    case 'sampleDoseTime': {
      return <SampleDoseTimeCell coordinate={coordinate} positionOffset={positionOffset} />;
    }
    case 'text':
    case 'number':
    default:
      return <DataTableDefaultCell coordinate={coordinate} positionOffset={positionOffset} />;
  }
};
